import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Flex from "../../../../../../../components/Flex";
import Input from "../../../../../../../components/Input";
import PasswordInput from "../../../../../../../components/app/PasswordInput";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useCreatePatientCredential } from "../../../../../../../queries/patient-chart/portal-access/use-create-patient-cred";
import CopyButton from "../../../../../../../utils/util-components/CopyButton";
import {
  getErrorSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";

interface CreatePatientAccessCredentialProps {
  disclosureHost: DisclosureHost;
  onSuccess?: () => void;
  viewMode?: boolean;
}

const CredentialFields = ({
  credentials,
}: {
  credentials: { userId: string; password: string };
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <Flex gap={1} width={"100%"} justifyContent={"space-between"}>
        <Box width={"100%"}>
          <Input
            label="User ID"
            textFieldProps={{
              name: "userId",
              value: credentials?.userId ?? "",
              InputProps: { disabled: true },
            }}
          />
        </Box>
        <CopyButton
          onClick={() => {
            navigator.clipboard.writeText(credentials?.userId ?? "");
          }}
        />
      </Flex>
    </Grid>
    <Grid item xs={12}>
      <Flex gap={1} width={"100%"}>
        <Box width={"100%"}>
          <PasswordInput
            label="Password"
            name="password"
            value={credentials?.password ?? ""}
          />
        </Box>
        <CopyButton
          onClick={() => {
            navigator.clipboard.writeText(credentials?.password ?? "");
          }}
        />
      </Flex>
    </Grid>
  </Grid>
);

const CreatePatientAccessCredential = ({
  disclosureHost,
  onSuccess,
  viewMode,
}: CreatePatientAccessCredentialProps) => {
  const { patientId } = useParams();
  const showSnackbar = useSnackbar();
  const { mutateAsync: generateCredential } = useCreatePatientCredential();
  const [credentials, setCredentials] = useState({
    userId: disclosureHost?.data?.userId ?? "",
    password: disclosureHost?.data?.password ?? "",
  });
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    if (patientId && !generated && !viewMode && disclosureHost.opened) {
      generateCredential({
        patientId: patientId,
        xTENANTID: getXtenantId(),
      })
        .then((res: any) => {
          const data = res?.data?.data;

          setCredentials({
            userId: data?.username ?? "",
            password: data?.password ?? "",
          });
          setGenerated(true);
          onSuccess?.();
        })
        .catch((err: any) => showSnackbar(getErrorSnackbar(err)))
        .finally(() => {
          setGenerated(false);
        });
    }
  }, [patientId, disclosureHost.opened]);

  // Update credentials when viewing existing data
  useEffect(() => {
    if (viewMode && disclosureHost?.data) {
      setCredentials({
        userId: disclosureHost.data?.userId ?? "",
        password: disclosureHost.data?.password ?? "",
      });
    }
  }, [viewMode, disclosureHost?.data]);

  function handleClose() {
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle={
        viewMode
          ? "View Patient Access Credential"
          : "Create Patient Access Credential"
      }
      dialogAction={
        viewMode ? (
          <StyledButtonV2 label="Close" onClick={handleClose} />
        ) : undefined
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "500px",
          height: "250px",
        },
      }}
    >
      <Box pt={2}>
        {viewMode ? (
          <CredentialFields credentials={credentials} />
        ) : !generated ? (
          <Typography variant="body2" textAlign="center">
            Generating credentials...
          </Typography>
        ) : (
          <CredentialFields credentials={credentials} />
        )}
      </Box>
    </StyledFormModal>
  );
};

export default CreatePatientAccessCredential;
